import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { addDays, format, startOfDay } from 'date-fns';
import {
  AlertCircle,
  Building2,
  Loader2,
  MapPinned,
  RefreshCw,
} from 'lucide-react';
import {
  getPosReservationContext,
  getPosReservations,
  updatePosReservationStatus,
} from '../../lib/api';
import AdvancedFiltersPanel from '../../components/reservationBook/AdvancedFiltersPanel';
import DatePickerHeader from '../../components/reservationBook/DatePickerHeader';
import FastFiltersBar from '../../components/reservationBook/FastFiltersBar';
import QuickActionsMenu from '../../components/reservationBook/QuickActionsMenu';
import ReservationCalendarView from '../../components/reservationBook/ReservationCalendarView';
import ReservationDetailsRow from '../../components/reservationBook/ReservationDetailsRow';
import ReservationListView from '../../components/reservationBook/ReservationListView';
import ReservationTablePlanView from '../../components/reservationBook/ReservationTablePlanView';
import ReservationTimelineView from '../../components/reservationBook/ReservationTimelineView';
import SearchBar from '../../components/reservationBook/SearchBar';
import SlotsOverviewView from '../../components/reservationBook/SlotsOverviewView';
import StatsCardsRow from '../../components/reservationBook/StatsCardsRow';
import ViewSelectorTabs from '../../components/reservationBook/ViewSelectorTabs';
import { Button } from '../../components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../components/ui/card';
import { Tabs, TabsContent } from '../../components/ui/tabs';
import {
  buildCsvRows,
  extractDiscountInfo,
  getReservationDateTime,
  getReservationGuestName,
  getReservationTimeValue,
  normalizeReservationStatus,
} from '../../components/reservationBook/utils';
import { toast } from 'sonner';

const DEFAULT_VIEW = 'timeline';
const STATUS_SEQUENCE = ['pending', 'confirmed', 'seated', 'completed', 'cancelled', 'no_show'];

function buildPresetRange(preset) {
  const today = startOfDay(new Date());

  switch (preset) {
    case 'tomorrow': {
      const tomorrow = addDays(today, 1);
      return { start: tomorrow, end: tomorrow };
    }
    case 'next7':
      return { start: today, end: addDays(today, 6) };
    case 'all':
      return { start: addDays(today, -30), end: addDays(today, 60) };
    case 'today':
    default:
      return { start: today, end: today };
  }
}

function coerceDateRange(rangeLike, fallbackDate = startOfDay(new Date())) {
  const startCandidate = rangeLike?.start || rangeLike?.from || fallbackDate;
  const endCandidate = rangeLike?.end || rangeLike?.to || null;

  return {
    start: startOfDay(startCandidate),
    end: endCandidate ? startOfDay(endCandidate) : null,
  };
}

function formatDateKey(date) {
  return format(date, 'yyyy-MM-dd');
}

function buildDateSummaryLabel(range) {
  if (!range?.start) return 'Kein Zeitraum';
  if (!range.end || formatDateKey(range.start) === formatDateKey(range.end)) {
    return format(range.start, 'EEEE, dd. MMM yyyy');
  }
  return `${format(range.start, 'dd. MMM yyyy')} - ${format(range.end, 'dd. MMM yyyy')}`;
}

function escapeCsvCell(value) {
  const normalized = String(value ?? '');
  return `"${normalized.replace(/"/g, '""')}"`;
}

function downloadCsv(filename, rows) {
  const csv = rows.map((row) => row.map(escapeCsvCell).join(';')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

function normalizeReservation(rawReservation, tableById) {
  const tableId = String(
    rawReservation?.table_id || rawReservation?.reservation_table_id || '',
  ).trim();
  const linkedTable = tableId ? tableById.get(tableId) : null;
  const reservationDateTime = getReservationDateTime(rawReservation);

  return {
    ...rawReservation,
    id: String(rawReservation?.id || `${rawReservation?.reservation_date || ''}-${rawReservation?.guest_name || ''}`),
    status: normalizeReservationStatus(rawReservation?.status),
    guest_name: getReservationGuestName(rawReservation),
    party_size: Number(rawReservation?.party_size || rawReservation?.covers || 0) || 0,
    duration_minutes: Number(rawReservation?.duration_minutes || 90) || 90,
    table_id: tableId || null,
    table_name:
      rawReservation?.table_name
      || rawReservation?.table?.name
      || linkedTable?.name
      || '',
    table: rawReservation?.table || linkedTable || null,
    origin:
      rawReservation?.origin
      || rawReservation?.source
      || (rawReservation?.is_walk_in ? 'walk-in' : 'online'),
    reservation_time:
      rawReservation?.reservation_time
      || rawReservation?.start_time
      || (reservationDateTime ? format(reservationDateTime, 'HH:mm:ss') : ''),
  };
}

function matchesStatusFilter(reservation, statusFilter) {
  if (statusFilter === 'all') return true;
  if (statusFilter === 'upcoming') {
    return ['pending', 'confirmed', 'seated'].includes(reservation.status);
  }
  return reservation.status === statusFilter;
}

function matchesSearchFilter(reservation, searchValue) {
  if (!searchValue) return true;

  const haystack = [
    reservation.guest_name,
    reservation.phone,
    reservation.customer_phone,
    reservation.email,
    reservation.table_name,
    reservation.notes,
    reservation.special_requests,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  return haystack.includes(searchValue.toLowerCase());
}

function buildStats(reservations) {
  const base = {
    total: reservations.length,
    totalGuests: reservations.reduce(
      (sum, reservation) => sum + Number(reservation.party_size || 0),
      0,
    ),
    pending: 0,
    confirmed: 0,
    seated: 0,
    completed: 0,
    cancelled: 0,
    noShow: 0,
  };

  reservations.forEach((reservation) => {
    switch (reservation.status) {
      case 'pending':
        base.pending += 1;
        break;
      case 'confirmed':
        base.confirmed += 1;
        break;
      case 'seated':
        base.seated += 1;
        break;
      case 'completed':
        base.completed += 1;
        break;
      case 'cancelled':
        base.cancelled += 1;
        break;
      case 'no_show':
        base.noShow += 1;
        break;
      default:
        break;
    }
  });

  return base;
}

export default function AdminReservations() {
  const [view, setView] = useState(DEFAULT_VIEW);
  const [dateFilter, setDateFilter] = useState('today');
  const [dateRange, setDateRange] = useState(() => buildPresetRange('today'));
  const [focusedDate, setFocusedDate] = useState(() => buildPresetRange('today').start);
  const [filters, setFilters] = useState({
    search: '',
    status: 'all',
    discount: false,
  });
  const [context, setContext] = useState(null);
  const [rawReservations, setRawReservations] = useState([]);
  const [selectedReservation, setSelectedReservation] = useState(null);
  const [lastStatusAction, setLastStatusAction] = useState(null);
  const [loadingContext, setLoadingContext] = useState(true);
  const [loadingReservations, setLoadingReservations] = useState(true);
  const [notConfigured, setNotConfigured] = useState(false);

  const activeRange = useMemo(() => {
    if (dateFilter === 'custom') {
      return coerceDateRange(dateRange, buildPresetRange('today').start);
    }
    return buildPresetRange(dateFilter);
  }, [dateFilter, dateRange]);

  const tableById = useMemo(() => {
    return new Map(
      (context?.tables || []).map((table) => [String(table.id), table]),
    );
  }, [context]);

  const reservations = useMemo(() => {
    return [...rawReservations]
      .map((reservation) => normalizeReservation(reservation, tableById))
      .sort((left, right) => {
        const leftDate = getReservationDateTime(left)?.getTime() || 0;
        const rightDate = getReservationDateTime(right)?.getTime() || 0;
        return leftDate - rightDate;
      });
  }, [rawReservations, tableById]);

  const filteredReservations = useMemo(() => {
    return reservations.filter((reservation) => {
      if (!matchesStatusFilter(reservation, filters.status)) return false;
      if (!matchesSearchFilter(reservation, filters.search)) return false;
      if (filters.discount && !extractDiscountInfo(reservation).hasDiscount) return false;
      return true;
    });
  }, [filters, reservations]);

  const availableStatusCounts = useMemo(() => {
    return buildStats(
      reservations.filter((reservation) => {
        if (!matchesSearchFilter(reservation, filters.search)) return false;
        if (filters.discount && !extractDiscountInfo(reservation).hasDiscount) return false;
        return true;
      }),
    );
  }, [filters.discount, filters.search, reservations]);

  const stats = useMemo(() => buildStats(filteredReservations), [filteredReservations]);

  const capacity = useMemo(() => {
    const totalSeats = (context?.tables || []).reduce((sum, table) => {
      return sum + Number(table?.max_seats || table?.capacity || table?.min_seats || 0);
    }, 0);
    return Math.max(1, totalSeats || 100);
  }, [context]);

  const loadContext = useCallback(async () => {
    setLoadingContext(true);
    try {
      const response = await getPosReservationContext();
      setContext(response || null);
      setNotConfigured(false);
    } catch (error) {
      if (error?.response?.status === 503) {
        setNotConfigured(true);
        setContext(null);
      } else {
        toast.error(
          error?.response?.data?.detail || 'Reservierungs-Kontext konnte nicht geladen werden.',
        );
      }
    } finally {
      setLoadingContext(false);
    }
  }, []);

  const loadReservations = useCallback(async () => {
    setLoadingReservations(true);
    try {
      const params = {
        date_from: formatDateKey(activeRange.start),
        date_to: formatDateKey(activeRange.end || activeRange.start),
      };
      const response = await getPosReservations(params);
      setRawReservations(Array.isArray(response) ? response : []);
      setNotConfigured(false);
    } catch (error) {
      if (error?.response?.status === 503) {
        setNotConfigured(true);
        setRawReservations([]);
      } else {
        toast.error(
          error?.response?.data?.detail || 'Reservierungen konnten nicht geladen werden.',
        );
      }
    } finally {
      setLoadingReservations(false);
    }
  }, [activeRange.end, activeRange.start]);

  useEffect(() => {
    loadContext();
  }, [loadContext]);

  useEffect(() => {
    loadReservations();
  }, [loadReservations]);

  useEffect(() => {
    if (!selectedReservation) return;
    const nextReservation = reservations.find(
      (reservation) => String(reservation.id) === String(selectedReservation.id),
    );
    if (!nextReservation) {
      setSelectedReservation(null);
      return;
    }
    setSelectedReservation(nextReservation);
  }, [reservations, selectedReservation]);

  const handleRefresh = useCallback(async () => {
    await Promise.allSettled([loadContext(), loadReservations()]);
  }, [loadContext, loadReservations]);

  const handleFilterChange = useCallback((key, value) => {
    setFilters((current) => ({
      ...current,
      [key]: value,
    }));
  }, []);

  const handleClearFilters = useCallback(() => {
    setFilters({
      search: '',
      status: 'all',
      discount: false,
    });
  }, []);

  const handleDateFilterChange = useCallback((nextFilter) => {
    const normalizedFilter = nextFilter === 'clear' ? 'today' : nextFilter;
    const nextRange = buildPresetRange(normalizedFilter);
    setDateFilter(normalizedFilter);
    setDateRange(nextRange);
    setFocusedDate(nextRange.start);
  }, []);

  const handleCustomRangeChange = useCallback((rangeLike) => {
    const nextRange = coerceDateRange(rangeLike, activeRange.start);
    setDateFilter('custom');
    setDateRange(nextRange);
    setFocusedDate(nextRange.start);
  }, [activeRange.start]);

  const handleStatusChange = useCallback(async (reservationId, nextStatus) => {
    const existingReservation = reservations.find(
      (reservation) => String(reservation.id) === String(reservationId),
    );
    if (!existingReservation || existingReservation.status === nextStatus) return;

    try {
      const updated = await updatePosReservationStatus(reservationId, nextStatus);
      setRawReservations((current) => current.map((reservation) => (
        String(reservation.id) === String(reservationId)
          ? { ...reservation, ...updated }
          : reservation
      )));
      setLastStatusAction({
        reservationId,
        previousStatus: existingReservation.status,
      });
      toast.success('Reservierungsstatus gespeichert.');
    } catch (error) {
      toast.error(
        error?.response?.data?.detail || 'Status konnte nicht aktualisiert werden.',
      );
    }
  }, [reservations]);

  const handleUndoStatusAction = useCallback(async () => {
    if (!lastStatusAction) return;

    try {
      const updated = await updatePosReservationStatus(
        lastStatusAction.reservationId,
        lastStatusAction.previousStatus,
      );
      setRawReservations((current) => current.map((reservation) => (
        String(reservation.id) === String(lastStatusAction.reservationId)
          ? { ...reservation, ...updated }
          : reservation
      )));
      setLastStatusAction(null);
      toast.success('Letzte Aenderung rueckgaengig gemacht.');
    } catch (error) {
      toast.error(
        error?.response?.data?.detail || 'Rueckgaengig konnte nicht ausgefuehrt werden.',
      );
    }
  }, [lastStatusAction]);

  const handleExport = useCallback(() => {
    if (!filteredReservations.length) {
      toast.info('Keine Reservierungen fuer den Export gefunden.');
      return;
    }

    const filename = `reservierungen-${formatDateKey(activeRange.start)}.csv`;
    downloadCsv(filename, buildCsvRows(filteredReservations));
    toast.success('CSV-Export erstellt.');
  }, [activeRange.start, filteredReservations]);

  const handlePlaceholderAction = useCallback((message) => {
    toast.info(message);
  }, []);

  const selectionActions = useMemo(() => {
    if (!selectedReservation) return [];
    return STATUS_SEQUENCE
      .filter((status) => status !== selectedReservation.status)
      .slice(0, 4);
  }, [selectedReservation]);

  const isLoading = loadingContext || loadingReservations;

  return (
    <div className="space-y-6 p-4 sm:p-6" data-testid="admin-reservations">
      <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#1a2f38]">Reservierungen</h1>
          <p className="mt-1 text-sm text-slate-500">
            Reservierungsbuch fuer POS und Admin auf Basis des Kundenapp-Workspaces.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            onClick={handleRefresh}
            disabled={isLoading}
            className="gap-2"
          >
            <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
            Aktualisieren
          </Button>
          <QuickActionsMenu
            onNewReservation={() => handlePlaceholderAction('Neue Reservierung wird spaeter direkt aus dem Kundenapp-Flow uebernommen.')}
            onNewWalkIn={() => handlePlaceholderAction('Walk-In-Erfassung ist hier als naechster Schritt vorgesehen.')}
            onExport={handleExport}
            onUndo={lastStatusAction ? handleUndoStatusAction : undefined}
          />
        </div>
      </div>

      <Card className="border-slate-200">
        <CardContent className="grid gap-3 p-4 md:grid-cols-3">
          <div className="flex items-start gap-3 rounded-xl border bg-slate-50/70 p-3">
            <MapPinned className="mt-0.5 h-4 w-4 text-slate-500" />
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                Standort
              </p>
              <p className="text-sm font-semibold text-slate-800">
                {context?.location_name || 'Noch nicht verbunden'}
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3 rounded-xl border bg-slate-50/70 p-3">
            <Building2 className="mt-0.5 h-4 w-4 text-slate-500" />
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                Workforce Projekt
              </p>
              <p className="text-sm font-semibold text-slate-800">
                {context?.project_ref || 'bhhhfdlwqhedxefudcdd'}
              </p>
            </div>
          </div>

          <div className="rounded-xl border bg-slate-50/70 p-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
              Aktiver Zeitraum
            </p>
            <p className="text-sm font-semibold text-slate-800">
              {buildDateSummaryLabel(activeRange)}
            </p>
          </div>
        </CardContent>
      </Card>

      {notConfigured ? (
        <Card className="border-l-4 border-l-amber-500">
          <CardContent className="flex items-start gap-3 p-5">
            <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-amber-500" />
            <div>
              <p className="font-semibold text-sm">Workforce-Bridge nicht konfiguriert</p>
              <p className="mt-1 text-sm text-slate-500">
                Fuer Reservierungen muss in den Admin-Einstellungen die Verbindung zum
                zweiten Supabase-Projekt inklusive Company-, Location- und Terminal-ID
                gesetzt sein.
              </p>
            </div>
          </CardContent>
        </Card>
      ) : null}

      <StatsCardsRow stats={stats} />

      <Card className="overflow-hidden border-slate-200 shadow-sm">
        <CardHeader className="gap-4 border-b bg-slate-50/50 pb-4">
          <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
            <div>
              <CardTitle>Reservations Workspace</CardTitle>
              <CardDescription>
                {filteredReservations.length} von {reservations.length} Reservierungen sichtbar
              </CardDescription>
            </div>

            <div className="flex w-full flex-col gap-2 lg:flex-row xl:w-auto">
              <SearchBar
                value={filters.search}
                onChange={(value) => handleFilterChange('search', value)}
              />
              <DatePickerHeader
                dateRange={dateRange}
                onDateRangeChange={handleCustomRangeChange}
              />
              <AdvancedFiltersPanel
                filters={filters}
                onFilterChange={handleFilterChange}
                onClear={handleClearFilters}
              />
            </div>
          </div>

          <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
            <FastFiltersBar
              mode="date"
              currentFilter={dateFilter}
              onFilterChange={handleDateFilterChange}
              dateRange={dateRange}
              onCustomRangeChange={handleCustomRangeChange}
            />
            <FastFiltersBar
              mode="status"
              currentFilter={filters.status}
              onFilterChange={(value) => handleFilterChange('status', value)}
              counts={availableStatusCounts}
            />
          </div>
        </CardHeader>

        <CardContent className="p-4">
          <Tabs value={view} onValueChange={setView} className="space-y-4">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
              <ViewSelectorTabs />
              <p className="text-xs text-slate-500">
                Fokus-Tag: {format(focusedDate, 'dd. MMM yyyy')} · {getReservationTimeValue(selectedReservation || {}) || 'Kein Eintrag gewaehlt'}
              </p>
            </div>

            <TabsContent value="timeline" className="mt-0">
              <div className="h-[620px]">
                <ReservationTimelineView
                  reservations={filteredReservations}
                  tables={context?.tables || []}
                  displayDate={focusedDate}
                  isLoading={loadingReservations}
                  onSelectReservation={setSelectedReservation}
                />
              </div>
            </TabsContent>

            <TabsContent value="list" className="mt-0">
              <div className="h-[620px]">
                <ReservationListView
                  reservations={filteredReservations}
                  onStatusChange={handleStatusChange}
                  onSelect={setSelectedReservation}
                  onSendFeedback={() => handlePlaceholderAction('Feedback-Erinnerungen werden im naechsten Schritt portiert.')}
                />
              </div>
            </TabsContent>

            <TabsContent value="calendar" className="mt-0">
              <div className="h-[620px]">
                <ReservationCalendarView
                  reservations={filteredReservations}
                  isLoading={loadingReservations}
                  onDateSelect={(date) => setFocusedDate(startOfDay(date))}
                  currentMonth={focusedDate}
                  capacity={capacity}
                />
              </div>
            </TabsContent>

            <TabsContent value="floor" className="mt-0">
              <div className="h-[620px]">
                <ReservationTablePlanView
                  tables={context?.tables || []}
                  reservations={filteredReservations}
                  displayDate={focusedDate}
                  isLoading={loadingReservations}
                  onSelectReservation={setSelectedReservation}
                />
              </div>
            </TabsContent>

            <TabsContent value="slots" className="mt-0">
              <div className="h-[620px]">
                <SlotsOverviewView
                  reservations={filteredReservations}
                  selectedDay={focusedDate}
                />
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {selectedReservation ? (
        <Card className="border-slate-200 shadow-sm">
          <CardHeader className="gap-3 border-b bg-slate-50/50 pb-4">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <CardTitle>{getReservationGuestName(selectedReservation)}</CardTitle>
                <CardDescription>
                  {buildDateSummaryLabel({
                    start: getReservationDateTime(selectedReservation) || focusedDate,
                    end: getReservationDateTime(selectedReservation) || focusedDate,
                  })}
                </CardDescription>
              </div>

              <div className="flex flex-wrap gap-2">
                {selectionActions.map((status) => (
                  <Button
                    key={status}
                    variant="outline"
                    size="sm"
                    onClick={() => handleStatusChange(selectedReservation.id, status)}
                  >
                    {status}
                  </Button>
                ))}
              </div>
            </div>
          </CardHeader>

          <CardContent className="p-0">
            <ReservationDetailsRow reservation={selectedReservation} />
          </CardContent>
        </Card>
      ) : null}

      {isLoading && !notConfigured ? (
        <div className="flex items-center justify-center py-10">
          <Loader2 className="h-8 w-8 animate-spin text-[#1a2f38]" />
        </div>
      ) : null}
    </div>
  );
}
